import type {WithBaselinePinnedFlags} from './types';
import {
  BackendLinesQueryResult,
  BackendLinesQueryRunsDelta,
  BucketedQueryResult,
  BucketedQueryRunsDelta,
} from './types';

/**
 * Helper function to add an ID to the merged order list with deduplication.
 * If the ID has already been processed, it's skipped (first occurrence wins).
 *
 * @param id - The run ID to add
 * @param source - The source of the run ('baseline', 'pinned', or 'regular')
 * @param processedIds - Set tracking which IDs have been processed
 * @param idSource - Map tracking the source of each ID
 * @param newOrder - Array to append the ID to if not already processed
 */
export function addIdToMergedOrder(
  id: string,
  source: 'baseline' | 'pinned' | 'regular',
  processedIds: Set<string>,
  idSource: Map<string, 'baseline' | 'pinned' | 'regular'>,
  newOrder: string[]
): void {
  if (!processedIds.has(id)) {
    processedIds.add(id);
    idSource.set(id, source);
    newOrder.push(id);
  }
}

/**
 * Builds an index (Map) of deltas keyed by run ID for O(1) lookup.
 *
 * @param deltas - Array of deltas to index
 * @returns Map from run ID to delta
 */
function buildDeltaIndex<T extends {run?: {id?: string | null} | null}>(
  deltas: ReadonlyArray<T>
): Map<string, T> {
  const index = new Map<string, T>();
  for (const delta of deltas) {
    if (delta.run?.id != null) {
      index.set(delta.run.id, delta);
    }
  }
  return index;
}

/**
 * Merges baseline, pinned, and regular runs from aliased GraphQL queries.
 *
 * The query fetches:
 * - baselineRun: Single baseline run (via aliased runs query)
 * - pinnedRuns: Multiple pinned runs (via aliased runs query)
 * - runs: Regular runs (main query)
 *
 * All queries return data in delta format. This function merges them in order
 * (baseline → pinned → regular) and marks each run with isBaseline/isPinned flags.
 *
 * Deduplicates across query types (baseline > pinned > regular priority).
 * Preserves all IDs from order arrays even when no corresponding delta exists
 * (applyDelta will use cached data for IDs without deltas).
 *
 * Generic to support both BucketedQueryResult and BackendLinesQueryResult.
 *
 * **Mutates the input result object** by modifying result.data.project.runs.deltas.
 *
 * @param result - The query result to merge
 * @param maxTotalRuns - Maximum total runs allowed (baseline + pinned + regular). Used to limit regular runs when backend ignores limit: 0.
 */
export function mergeBaselineAndPinnedRuns<
  T extends BucketedQueryResult | BackendLinesQueryResult,
>(result: T, maxTotalRuns?: number): void {
  const project = result?.data?.project;
  if (!project) {
    return;
  }

  const baselineRunQuery = project.baselineRun;
  const pinnedRunsQuery = project.pinnedRuns;
  const mainRuns = project.runs;

  if (!mainRuns) {
    return;
  }

  type MergeableDelta = BucketedQueryRunsDelta | BackendLinesQueryRunsDelta;

  const createEmptyDeltas = (): NonNullable<typeof mainRuns.deltas> => ({
    __typename: 'BucketDiffs',
    delta: [],
    order: [],
  });

  let mainRunsDeltas = mainRuns.deltas;
  if (
    !mainRunsDeltas ||
    !Array.isArray(mainRunsDeltas.delta) ||
    !Array.isArray(mainRunsDeltas.order)
  ) {
    mainRunsDeltas = createEmptyDeltas();
    mainRuns.deltas = mainRunsDeltas;
  }

  // Early exit if already merged and run flags already added (e.g. when pulling from Apollo cache)
  if ((mainRunsDeltas as any).runFlags !== undefined) {
    return;
  }

  // Extract deltas and order arrays from query responses
  const baselineDeltas: ReadonlyArray<MergeableDelta> = (baselineRunQuery
    ?.deltas?.delta ?? []) as ReadonlyArray<MergeableDelta>;
  const baselineOrder = baselineRunQuery?.deltas?.order ?? [];
  const pinnedDeltas: ReadonlyArray<MergeableDelta> = (pinnedRunsQuery?.deltas
    ?.delta ?? []) as ReadonlyArray<MergeableDelta>;
  const pinnedOrder = pinnedRunsQuery?.deltas?.order ?? [];
  const regularDeltas: ReadonlyArray<MergeableDelta> =
    mainRunsDeltas.delta as MergeableDelta[];
  const regularOrder = mainRunsDeltas.order;

  // Apply maxTotalRuns limit to regular runs if specified
  if (maxTotalRuns !== undefined) {
    // Account for baseline also being pinned (avoid double-counting)
    const baselineId = baselineOrder[0];
    const baselineAlsoPinned = baselineId && pinnedOrder.includes(baselineId);
    const uniqueBaselinePinnedCount =
      baselineOrder.length + pinnedOrder.length - (baselineAlsoPinned ? 1 : 0);

    const maxRegularRuns = Math.max(
      0,
      maxTotalRuns - uniqueBaselinePinnedCount
    );
    if (regularOrder.length > maxRegularRuns) {
      regularOrder.length = maxRegularRuns;
    }
  }

  // STEP 1: Build merged, deduplicated order list
  const processedIds = new Set<string>();
  const idSource = new Map<string, 'baseline' | 'pinned' | 'regular'>();
  const newOrder: string[] = [];

  // Add baseline, pinned, and regular IDs (in priority order)
  baselineOrder.forEach(id =>
    addIdToMergedOrder(id, 'baseline', processedIds, idSource, newOrder)
  );
  pinnedOrder.forEach(id =>
    addIdToMergedOrder(id, 'pinned', processedIds, idSource, newOrder)
  );
  regularOrder.forEach(id =>
    addIdToMergedOrder(id, 'regular', processedIds, idSource, newOrder)
  );

  // Index deltas by run ID for O(1) lookup instead of O(N) linear search
  const baselineDeltasById = buildDeltaIndex(baselineDeltas);
  const pinnedDeltasById = buildDeltaIndex(pinnedDeltas);
  const regularDeltasById = buildDeltaIndex(regularDeltas);

  // STEP 2: Build deltas list from merged order
  const newDeltas: MergeableDelta[] = [];

  for (const idStr of newOrder) {
    const source = idSource.get(idStr)!;

    // Find delta based on source (baseline > pinned > regular priority)
    // O(1) Map lookup instead of O(N) array search
    let delta: MergeableDelta | undefined;
    if (source === 'baseline') {
      delta = baselineDeltasById.get(idStr);
    } else if (source === 'pinned') {
      delta = pinnedDeltasById.get(idStr);
    } else {
      delta = regularDeltasById.get(idStr);
    }

    // Add delta with flags if it exists
    if (delta?.run) {
      const run = delta.run as WithBaselinePinnedFlags<typeof delta.run>;
      run.isBaseline = source === 'baseline';
      run.isPinned = source === 'pinned';
      newDeltas.push(delta);
    }
    // If no delta, that's fine - applyDelta will use cached data for this ID
  }

  // Replace arrays with new merged arrays
  // This mutates the original arrays, preserving references
  (mainRunsDeltas.delta as MergeableDelta[]).length = 0;
  (mainRunsDeltas.delta as MergeableDelta[]).push(...newDeltas);
  mainRunsDeltas.order.length = 0;
  mainRunsDeltas.order.push(...newOrder);

  // Store flag information for applyDelta to use on cached runs
  // This ensures flags are updated even when there's no delta for a run
  (mainRunsDeltas as any).runFlags = [...idSource.entries()].reduce(
    (flags, [id, source]) => {
      flags[id] = {
        isBaseline: source === 'baseline',
        isPinned: source === 'pinned',
      };
      return flags;
    },
    {} as Record<string, {isBaseline: boolean; isPinned: boolean}>
  );
}
